<template>
  <transition name="slide-down">
    <div
      v-if="shouldShow"
      :class="statusBarClasses"
      class="w-full border-b shadow-sm transition-all duration-300"
      role="alert"
    >
      <div class="container mx-auto px-4 py-3 max-w-7xl flex items-center justify-center gap-4">
        <!-- Icon based on type -->
        <div class="flex-shrink-0">
          <svg v-if="statusBar.type === 'info'" class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
          </svg>
          <svg v-else-if="statusBar.type === 'success'" class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
          </svg>
          <svg v-else-if="statusBar.type === 'warning'" class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
          </svg>
          <svg v-else-if="statusBar.type === 'error'" class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
          </svg>
        </div>

        <!-- Message content -->
        <div class="flex-1 text-sm sm:text-base font-medium" v-html="statusBar.message"></div>

        <!-- Dismiss button -->
        <button
          v-if="statusBar.dismissible"
          @click="dismiss"
          class="flex-shrink-0 hover:opacity-70 transition-opacity"
          aria-label="Dismiss notification"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
          </svg>
        </button>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'StatusBar',
  props: {
    statusBar: {
      type: Object,
      default: () => ({
        isVisible: false,
        message: '',
        type: 'info',
        dismissible: true,
        messageId: null
      })
    }
  },
  data() {
    return {
      locallyDismissed: false
    };
  },
  computed: {
    shouldShow() {
      if (!this.statusBar.isVisible || !this.statusBar.message) return false;
      if (this.locallyDismissed) return false;

      // Check if user dismissed this specific message
      const dismissedId = localStorage.getItem('dismissedStatusBar');
      return dismissedId !== this.statusBar.messageId;
    },
    statusBarClasses() {
      const types = {
        info: 'bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 border-blue-300 dark:border-blue-700',
        success: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200 border-emerald-300 dark:border-emerald-700',
        warning: 'bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 border-amber-300 dark:border-amber-700',
        error: 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-red-300 dark:border-red-700'
      };
      return types[this.statusBar.type] || types.info;
    }
  },
  methods: {
    dismiss() {
      this.locallyDismissed = true;
      localStorage.setItem('dismissedStatusBar', this.statusBar.messageId);
    }
  },
  watch: {
    'statusBar.messageId'(newId, oldId) {
      // Reset dismissal when message changes
      if (newId !== oldId) {
        this.locallyDismissed = false;
      }
    }
  }
};
</script>

<style scoped>
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from {
  transform: translateY(-100%);
  opacity: 0;
}

.slide-down-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}
</style>
